// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Header, Button, Form, Message, Icon, Segment } from 'semantic-ui-react';
import { templateApi, LOATemplate } from '../api/templateApi';

function TemplateFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<LOATemplate>({
    name: '',
    code: '',
    description: '',
    isDefault: false
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isEditMode && id) {
      loadTemplate(id);
    }
  }, [id, isEditMode]);

  const loadTemplate = async (templateId: string) => {
    try {
      setLoading(true);
      const data = await templateApi.getById(templateId);
      setFormData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Only PDF files are allowed');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      if (isEditMode && id) {
        // Update metadata only
        await templateApi.updateMetadata(id, {
          name: formData.name,
          code: formData.code,
          description: formData.description,
          isDefault: formData.isDefault
        });
      } else {
        // Upload new template
        if (!selectedFile) {
          setError('Please select a PDF file to upload');
          setLoading(false);
          return;
        }

        await templateApi.upload(selectedFile, {
          name: formData.name,
          code: formData.code,
          description: formData.description,
          isDefault: formData.isDefault
        });
      }

      setSuccess(true);
      setTimeout(() => navigate('/templates'), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container style={{ marginTop: '50px' }}>
      <Button basic onClick={() => navigate('/templates')} style={{ marginBottom: '20px' }}>
        <Icon name='arrow left' /> Back to Templates
      </Button>

      <Header as='h1'>
        <Icon name='file pdf' />
        <Header.Content>
          {isEditMode ? 'Edit Template Metadata' : 'Upload New Template'}
        </Header.Content>
      </Header>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {success && (
        <Message positive>
          <Message.Header>Success</Message.Header>
          <p>Template {isEditMode ? 'updated' : 'uploaded'} successfully!</p>
        </Message>
      )}

      <Segment>
        <Form loading={loading}>
          {!isEditMode && (
            <>
              <Form.Field required>
                <label htmlFor="template-pdf-file">PDF File</label>
                <Button
                  icon
                  labelPosition='left'
                  onClick={() => fileInputRef.current?.click()}
                  type='button'
                >
                  <Icon name='file pdf' />
                  {selectedFile ? selectedFile.name : 'Select PDF File'}
                </Button>
                <input
                  id="template-pdf-file"
                  ref={fileInputRef}
                  type='file'
                  accept='application/pdf'
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
                {selectedFile && (
                  <div style={{ marginTop: '10px', color: '#666' }}>
                    <Icon name='check circle' color='green' />
                    {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </div>
                )}
              </Form.Field>

              <Message info>
                <Message.Header>File Requirements</Message.Header>
                <Message.List>
                  <Message.Item>PDF format only</Message.Item>
                  <Message.Item>Maximum file size: 10 MB</Message.Item>
                  <Message.Item>File will be stored securely on the server</Message.Item>
                </Message.List>
              </Message>
            </>
          )}

          {isEditMode && formData.fileUrl && (
            <Message>
              <Message.Header>Current File</Message.Header>
              <p>
                <Icon name='file pdf outline' />
                {formData.fileUrl} ({(formData.fileSize || 0) / 1024} KB)
              </p>
              <p style={{ color: '#666', fontSize: '0.9em' }}>
                Note: To upload a new file, please create a new template
              </p>
            </Message>
          )}

          <Form.Group widths='equal'>
            <Form.Input
              label='Name'
              placeholder='Template name (e.g., Standard LOA Template)'
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <Form.Input
              label='Code'
              placeholder='Uppercase code (e.g., STANDARD_LOA)'
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              required
              disabled={isEditMode}
            />
          </Form.Group>

          <Form.TextArea
            label='Description'
            placeholder='Template description'
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />

          <Form.Checkbox
            label='Set as Default Template'
            checked={formData.isDefault}
            onChange={(_e, data) => setFormData({ ...formData, isDefault: !!data.checked })}
          />

          <Button
            primary
            onClick={handleSubmit}
            disabled={!formData.name || !formData.code || (!isEditMode && !selectedFile) || loading}
          >
            <Icon name='save' /> {isEditMode ? 'Update Metadata' : 'Upload Template'}
          </Button>
          <Button onClick={() => navigate('/templates')}>Cancel</Button>
        </Form>
      </Segment>
    </Container>
  );
}

export default TemplateFormPage;
